import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Phone, Clock, MapPin, Linkedin, Twitter, Github, Check, Send } from "lucide-react";
import { GlowButton } from "@/components/GlowButton";
import { GlassCard } from "@/components/GlassCard";
import { SectionHeader } from "@/components/SectionHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useSeo } from "@/hooks/useSeo";
import { PROJECT_TYPES, BUDGETS } from "@/lib/data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const container = "mx-auto max-w-6xl px-4 sm:px-6 lg:px-8";

const initialForm = {
  name: "",
  company: "",
  email: "",
  phone: "",
  project_type: "",
  budget: "",
  message: "",
  website: "", // honeypot
};

export default function Contact() {
  useSeo({
    title: "Contact — Métis Tech Labs",
    description:
      "Book a consultation with Métis Tech Labs. Tell us about your AI, automation, dashboard, or custom software project.",
  });

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    if (!form.email.trim()) e.email = "Please enter your email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.message.trim()) e.message = "Tell us a bit about your project";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      toast.success("Message sent — we’ll be in touch shortly.");
      setForm(initialForm);
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const info = [
    { icon: Mail, label: "Email", value: "hello@metistechlabs.com", href: "mailto:hello@metistechlabs.com" },
    { icon: Phone, label: "Phone", value: "+1 (415) 555-0142", href: "tel:+14155550142" },
    { icon: Clock, label: "Business Hours", value: "Mon – Fri • 9:00–18:00 PT" },
    { icon: MapPin, label: "Location", value: "Remote-first • San Francisco, CA" },
  ];
  const socials = [
    { icon: Linkedin, href: "#", label: "LinkedIn" },
    { icon: Twitter, href: "#", label: "Twitter" },
    { icon: Github, href: "#", label: "GitHub" },
  ];

  return (
    <div className="pt-32">
      <section className={container}>
        <SectionHeader
          eyebrow="Contact"
          title="Let’s build something intelligent"
          subtitle="Share your goals and we’ll respond within one business day."
        />

        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* LEFT: info */}
          <div className="lg:col-span-5">
            <GlassCard glow="purple" sheen={false} className="h-full p-7">
              <h3 className="font-display text-xl font-semibold text-white">Contact information</h3>
              <p className="mt-2 text-sm text-muted-brand">
                Prefer a direct line? Reach us through any channel below.
              </p>
              <div className="mt-7 space-y-5">
                {info.map((it) => (
                  <div key={it.label} className="flex items-start gap-3.5">
                    <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl glass text-neon-g">
                      <it.icon size={17} />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-[0.14em] text-muted-brand">{it.label}</div>
                      {it.href ? (
                        <a href={it.href} className="text-sm text-white transition-colors hover:text-neon-g">{it.value}</a>
                      ) : (
                        <div className="text-sm text-white">{it.value}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 border-t border-[rgba(255,255,255,0.08)] pt-6">
                <div className="mb-3 text-xs uppercase tracking-[0.14em] text-muted-brand">Follow</div>
                <div className="flex gap-3">
                  {socials.map((s) => (
                    <a key={s.label} href={s.href} aria-label={s.label} className="flex h-10 w-10 items-center justify-center rounded-full glass text-muted-brand transition-all duration-300 hover:text-neon-g hover:shadow-[var(--glow-g)]">
                      <s.icon size={16} />
                    </a>
                  ))}
                </div>
              </div>
            </GlassCard>
          </div>

          {/* RIGHT: form */}
          <div className="lg:col-span-7">
            <GlassCard glow="green" sheen={false} className="relative overflow-hidden p-7">
              <AnimatePresence mode="wait">
                {success ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex min-h-[420px] flex-col items-center justify-center text-center"
                    data-testid="contact-success"
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 200, damping: 14 }}
                      className="relative flex h-20 w-20 items-center justify-center rounded-full bg-[rgba(57,255,20,0.12)]"
                    >
                      <span className="absolute inset-0 animate-ping rounded-full bg-[rgba(57,255,20,0.18)]" />
                      <Check size={38} className="text-neon-g" />
                    </motion.div>
                    <h3 className="mt-6 font-display text-2xl font-semibold text-white">Message received</h3>
                    <p className="mt-2 max-w-sm text-muted-brand">
                      Thanks for reaching out. Our team will respond within one business day.
                    </p>
                    <GlowButton variant="secondary" className="mt-7" onClick={() => setSuccess(false)} data-testid="contact-send-another-button">
                      Send another message
                    </GlowButton>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onSubmit={onSubmit}
                    noValidate
                    data-testid="contact-form"
                  >
                    {/* Honeypot */}
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={(e) => update("website", e.target.value)}
                      className="hidden"
                      aria-hidden="true"
                    />

                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                      <Field label="Name" error={errors.name} required>
                        <Input data-testid="contact-input-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Jane Doe" className="brand-input" />
                      </Field>
                      <Field label="Company">
                        <Input data-testid="contact-input-company" value={form.company} onChange={(e) => update("company", e.target.value)} placeholder="Acme Inc." className="brand-input" />
                      </Field>
                      <Field label="Email" error={errors.email} required>
                        <Input data-testid="contact-input-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="jane@company.com" className="brand-input" />
                      </Field>
                      <Field label="Phone">
                        <Input data-testid="contact-input-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+1 (555) 000-0000" className="brand-input" />
                      </Field>
                      <Field label="Project Type">
                        <Select value={form.project_type} onValueChange={(v) => update("project_type", v)}>
                          <SelectTrigger data-testid="contact-select-project-type" className="brand-input">
                            <SelectValue placeholder="Select a type" />
                          </SelectTrigger>
                          <SelectContent>
                            {PROJECT_TYPES.map((t) => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="Budget">
                        <Select value={form.budget} onValueChange={(v) => update("budget", v)}>
                          <SelectTrigger data-testid="contact-select-budget" className="brand-input">
                            <SelectValue placeholder="Select a range" />
                          </SelectTrigger>
                          <SelectContent>
                            {BUDGETS.map((b) => (
                              <SelectItem key={b} value={b}>{b}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                    </div>

                    <div className="mt-5">
                      <Field label="Message" error={errors.message} required>
                        <Textarea data-testid="contact-input-message" value={form.message} onChange={(e) => update("message", e.target.value)} rows={5} placeholder="Tell us about your goals, timeline, and what success looks like." className="brand-input resize-none" />
                      </Field>
                    </div>

                    <div className="mt-7">
                      <GlowButton type="submit" pulse disabled={submitting} className="w-full sm:w-auto" data-testid="contact-submit-button">
                        {submitting ? "Sending…" : (<>Send Message <Send size={16} /></>)}
                      </GlowButton>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>
            </GlassCard>
          </div>
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, error, required, children }) => (
  <div className="flex flex-col gap-1.5">
    <Label className="text-xs uppercase tracking-[0.12em] text-muted-brand">
      {label} {required && <span className="text-neon-g">*</span>}
    </Label>
    {children}
    {error && <span className="text-xs text-[color:#ff6b6b]">{error}</span>}
  </div>
);
